import { API_CONFIG, apiClient } from '../api/config';
import { logger } from '../utils/logger';

class ActivityTracker {
  constructor() {
    this.activities = [];
    this.maxBatchSize = 3;
    this.userId = '0'; // 默认用户ID guest
    this.ipAddress = '0.0.0.0'; // 默认IP
    this.fetchIpAddress(); // 构造函数中获取IP
  }

  // 新方法：获取客户端IP
  async fetchIpAddress() {
    try {
      // 使用免费的IP查询服务
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      this.ipAddress = data.ip || '0.0.0.0';
      logger.log('获取到客户端IP:', this.ipAddress);
    } catch (error) {
      logger.error('获取IP地址失败:', error);
      // 保持默认IP
    }
  }

  // 直接设置用户ID
  setUserId(userId) {
    this.userId = userId || '0';
    console.log('设置用户ID:', this.userId);
  }
  
  // 新方法：通过邮箱获取用户ID并设置
  async fetchUserIdByEmail(email) {
    if (!email) {
      logger.warn('未提供邮箱，无法获取用户ID');
      return;
    }
    
    try {
      const response = await apiClient.get(API_CONFIG.USER_ACTIVITY.GET_USER);
      
      if (response.data && response.data.user_id) {
        this.setUserId(response.data.user_id);
        logger.log('已获取并设置用户ID:', this.userId);
      } else {
        logger.warn('获取用户ID失败，使用默认ID');
      }
    } catch (error) {
      logger.error('获取用户ID错误:', error);
      // 保持默认ID
    }
  }

  trackActivity(activityData) {
    logger.debug('trackActivity 被调用，接收到的数据:', activityData);
    logger.debug('ActivityTracker: 追踪活动', {
      actionType: activityData.actionType,
      symbol: activityData.symbol,
      pageUrl: window.location.pathname
    });

    const activity = {
      user_id: this.userId,
      target_symbol: activityData.symbol || '',
      action_type: activityData.actionType,
      page_url: window.location.pathname,
      query_params: JSON.stringify(this.getQueryParams()),
      login_info: JSON.stringify(navigator.userAgent),
      ip_address: this.ipAddress,
      timestamp: new Date().toISOString()
    };

    logger.debug('准备发送到服务器的活动数据:', activity);

    this.activities.push(activity);
    
    if (this.activities.length >= this.maxBatchSize) {
      this.sendActivities();
    }
  }

  trackSearch(symbol) {
    this.trackActivity({
      actionType: 'CompanySearch',
      symbol
    });
  }

  trackPageView(symbol) {
    this.trackActivity({
      actionType: 'PageView',
      symbol
    });
  }

  trackBtnExecution(action, symbol, pageUrl) {
    console.log('trackBtnExecution 被调用:', { action, symbol, pageUrl });
    this.trackActivity({
      actionType: action,
      symbol: symbol,
      pageUrl: pageUrl
    });
  }
  
  trackFileExecution(fileName) {
    this.trackActivity({
      actionType: 'FileExecution',
      symbol: fileName
    });
  }

  getQueryParams() {
    const queryParams = {};
    const search = window.location.search.substring(1);
    const pairs = search.split('&');
    
    for (let i = 0; i < pairs.length; i++) {
      if (!pairs[i]) continue;
      
      const pair = pairs[i].split('=');
      queryParams[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
    }
    
    return queryParams;
  }

  async sendActivities() {
    if (this.activities.length === 0) return;
    
    const activitiesToSend = [...this.activities];
    this.activities = [];
    
    try {
      console.log('ActivityTracker: 发送活动数据', {
        activities: activitiesToSend,
        pageUrl: window.location.pathname
      });

      // 使用 apiClient 发送请求，不需要手动添加 token
      await apiClient.post(
        API_CONFIG.USER_ACTIVITY.TRACK, 
        {
          activities: activitiesToSend,
          client_ip: this.ipAddress,
          user_id: this.userId
        }
      );
      
      logger.log(`Successfully sent ${activitiesToSend.length} activity logs`);
    } catch (error) {
      console.error('ActivityTracker: 发送活动数据失败', {
        error: error.message,
        activities: activitiesToSend,
        status: error.response?.status
      });
      
      // 如果是认证错误，不要重试
      if (error.response?.status === 401) {
        logger.warn('活动数据发送失败：未认证');
        return;
      }
      
      // 其他错误则重试
      logger.error('Failed to send activity logs:', error);
      this.activities = [...activitiesToSend, ...this.activities];
    }
  }
}

// 创建单例实例
const activityTracker = new ActivityTracker();

// 修改beforeunload事件处理程序
window.addEventListener('beforeunload', () => {
  if (activityTracker.activities.length > 0) {
    try {
      const data = new Blob([JSON.stringify({ 
        activities: activityTracker.activities,
        client_ip: activityTracker.ipAddress,
        user_id: activityTracker.userId
      })], { type: 'application/json' });

      // 使用 sendBeacon API，这是为页面卸载场景设计的
      const success = navigator.sendBeacon(
        `${API_CONFIG.baseURL}${API_CONFIG.USER_ACTIVITY.TRACK}`,
        data
      );
      
      if (!success) {
        console.warn('ActivityTracker: 无法通过 sendBeacon 发送活动数据');
        logger.warn('无法通过 sendBeacon 发送活动数据');
      }
    } catch (error) {
      console.error('ActivityTracker: 发送活动数据时出错', error);
      logger.error('发送活动数据时出错:', error);
    }
  }
});

// 导出默认实例
export default activityTracker; 